import { useEffect, useMemo, useRef, useState } from "react";
import { formatUzPhone, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronRight, FileSearch, Plus, Trash2, X } from "lucide-react";
import {
  api,
  PAYMENT_METHODS,
  type ClientDetails,
  type EmployeeRow,
  type PaymentMethod,
  type PaymentMethodsOut,
  type PaymentStatus,
  type Product,
  type ServiceOut,
  type WorkDetail,
  type WorkPayment,
  type WorkRow,
  type WorksPaymentSummary,
} from "../api";
import { downloadFile } from "../api";
import { TZ, fmtDateLong, fmtInt, fmtMoney, parseServerDate } from "../lib/format";
import { useFeature } from "../lib/use-tariff";
import { useBranch } from "../lib/use-branch";
import { ExportButton } from "../components/export-button";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { MoneyInput } from "../components/money-input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { ProductCombobox } from "../components/product-combobox";
import { DateColumn, buildDateGroups } from "../components/date-ledger";
import { PageFilterPopover } from "../components/page-filter-popover";
import { PaymentMethodBreakdown } from "../components/payment-method-breakdown";
import { MultiSelectDropdown } from "../components/ui/multi-select-dropdown";
import {
  WorkPaymentValue,
  WorkPaymentCorrectionDialog,
} from "../components/payment-method-correction";
import { usePageHeader } from "../lib/use-page-header";
import { Plate, Mono } from "../components/ui/ledger";
import { cn } from "../lib/utils";
import { selectedServiceTotal, toggleUnique } from "../lib/work-selection";
import {
  aggregatePaid,
  displayedSingleAmount,
  invalidPaymentLines,
  paymentPayload,
  persistedPaidTotal,
  togglePaymentLine,
  type WorkPaymentLine,
} from "../lib/work-payments";

type Filters = {
  date_from: string;
  date_to: string;
  service_id: string;
  user_id: string;
  tolov_holati: string;
};

const EMPTY_FILTERS: Filters = {
  date_from: "",
  date_to: "",
  service_id: "",
  user_id: "",
  tolov_holati: "",
};

const PAGE = 200;

function fmtTime(iso: string): string {
  return parseServerDate(iso).toLocaleTimeString("uz-UZ", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Ish umumiy summasi = ish haqi + biriktirilgan tovarlar. */
function workTotal(w: WorkRow): number {
  return Number(w.price) + Number(w.parts_total);
}

/**
 * Bitta ishning qarzi (ish haqi + tovarlar − to'langan). To'liq to'langan
 * ("tolandi") yoki bekor qilingan ishda qarz 0 — backend mantiqiga mos
 * (work_service: faqat `tolov_holati !== tolandi` bo'lganda qarz yoziladi).
 *
 * `tolov_holati` tekshiruvi MAJBURIY: bu o'zgarishdan oldingi to'langan
 * yozuvlarda `tolangan_summa` 0 bo'lib qolgan, shartsiz ayirma esa ularni
 * to'liq qarzdek ko'rsatib yuborardi.
 */
function workDebt(w: WorkRow): number {
  if (w.status !== "active" || w.tolov_holati === "tolandi") return 0;
  return Math.max(0, workTotal(w) - Number(w.tolangan_summa));
}

function paymentLabel(status: PaymentStatus, t: TFn): string {
  return status === "tolandi"
    ? t("form.paid")
    : status === "qisman"
      ? t("form.partial")
      : t("form.unpaid");
}

function paymentBadge(status: PaymentStatus, label: string) {
  const variant = status === "tolandi" ? "success" : status === "qisman" ? "secondary" : "destructive";
  return <Badge variant={variant}>{label}</Badge>;
}

export function WorksPage({
  onOpenDebts,
  focusWork,
}: {
  onOpenDebts?: () => void;
  /**
   * Tovar jurnalidan kelingan ish (ishga biriktirilgan tovar satri).
   * `seq` — bir xil ish qayta bosilsa ham qayta ochilsin.
   */
  focusWork?: { id: number; seq: number } | null;
}) {
  const { t } = useTranslation(["works", "common"]);
  const { selectedBranchId } = useBranch();
  // Qarz daftari yo'q tarifda to'lov ustuni/filtri/xulosasi UMUMAN
  // ko'rsatilmaydi — bo'sh ustun emas, yo'q.
  const hasDebts = useFeature("qarzlar");
  const [items, setItems] = useState<WorkRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [summary, setSummary] = useState<WorksPaymentSummary | null>(null);
  // Usul bo'linishi — `summary` dan farqli, tarifga bog'liq EMAS.
  const [methods, setMethods] = useState<PaymentMethodsOut | null>(null);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [services, setServices] = useState<ServiceOut[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  // Inline tafsilot: ochilgan ish + lazy-cache (id → detail | "loading").
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, WorkDetail | "loading">>({});
  // Usulni to'g'rilash — to'lov holatidan QAT'I NAZAR ochiladi. Mijoz nomi
  // `WorkDetail` da yo'q, shuning uchun qatordan olib qo'shib qo'yamiz.
  const [methodEdit, setMethodEdit] = useState<{
    id: number;
    party: string;
    current: WorkPayment[];
    totalPaid: string;
  } | null>(null);
  // Tovar jurnalidan kelingan ish: ro'yxatni o'sha ishning vaqtigacha kesamiz,
  // shunda u eng yangi qator bo'lib birinchi sahifaga tushadi.
  const [focusCutoff, setFocusCutoff] = useState<string | null>(null);
  // Qayta yuklashdan KEYIN ochilishi kerak bo'lgan ish. Ref shart: ro'yxat
  // yuklab bo'lgach `setExpandedId(null)` qiladi va oddiy state bu tanlovni
  // yuvib yuborardi (filtr o'zgarishi aynan qayta yuklashni keltirib chiqaradi).
  const pendingExpandRef = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setServices(await api.services(true));
      } catch {
        // optional metadata, skip
      }
      try {
        setEmployees(await api.employees());
      } catch {
        // optional metadata, skip
      }
    })();
  }, []);

  const queryParams = useMemo(
    () => ({
      date_from: filters.date_from || undefined,
      // Fokus kesimi foydalanuvchi filtridan pastroq turadi: u o'z sanasini
      // qo'ysa, jurnaldan kelgan kesim yo'q bo'ladi.
      date_to: filters.date_to || focusCutoff || undefined,
      service_id: filters.service_id || undefined,
      user_id: filters.user_id || undefined,
      tolov_holati: filters.tolov_holati || undefined,
      branch_id: selectedBranchId ?? undefined,
    }),
    [filters, focusCutoff, selectedBranchId],
  );

  // Filtr / filial / reload o'zgarsa — birinchi sahifadan qaytadan yuklaymiz.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.works({ page: 1, page_size: PAGE, ...queryParams });
        setItems(r.items);
        setTotal(r.total);
        setPage(1);
        setExpandedId(pendingExpandRef.current);
        pendingExpandRef.current = null;
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("errors.generic"));
      } finally {
        setLoading(false);
      }
    })();
  }, [queryParams, reload, t]);

  // To'lov xulosasi — ro'yxat bilan bir xil filtr. Alohida so'rov, chunki
  // ro'yxat generic `Page<WorkRow>` qaytaradi va qo'shimcha maydon sig'maydi.
  useEffect(() => {
    if (!hasDebts) {
      setSummary(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const s = await api.worksPaymentSummary(queryParams);
        if (active) setSummary(s);
      } catch {
        if (active) setSummary(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [queryParams, reload, hasDebts]);

  // Usul bo'linishi — ro'yxat bilan AYNAN bir xil filtr. `hasDebts` bilan
  // gate QILINMAYDI: usul barcha tariflarda mavjud (yangi bayroq yo'q).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const m = await api.worksPaymentMethods(queryParams);
        if (active) setMethods(m);
      } catch {
        if (active) setMethods(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [queryParams, reload]);

  // Sana guruhlari — kunlik jami ish haqi + biriktirilgan tovarlardan, ya'ni
  // qatordagi summa bilan bir xil bazadan (avval faqat ish haqi olinar edi,
  // shu sababli kunlik jami qatorlar yig'indisiga to'g'ri kelmasdi).
  //
  // Bekor qilingan ish jamiga QO'SHILMAYDI — hujjat ro'yxatda ko'rinib turadi
  // (o'chirilgan holatda), lekin puli hisoblanmaydi. Sotuvlar sahifasi ham
  // aynan shunday ishlaydi, backend analitikasi ham (`analytics_scope`); aks
  // holda kunlik sarlavha ekranning o'zidagi qarz ustuni bilan ham,
  // analitika sahifasi bilan ham zid raqam ko'rsatardi.
  const groups = useMemo(
    () =>
      buildDateGroups(items, (w) => w.created_at, (w) =>
        w.status === "active" ? workTotal(w) : 0,
      ),
    [items],
  );

  useEffect(() => {
    if (groups.length && (selectedDate === null || !groups.some((g) => g.key === selectedDate))) {
      setSelectedDate(groups[0].key);
    }
  }, [groups, selectedDate]);

  // Tovar jurnalidan kelingan ish.
  //
  // Nega ro'yxatga qo'shib qo'yilmaydi (xarid/sotuv sahifalaridagidek):
  // `api.getWork` ro'yxat qatoridan BOSHQA shakl qaytaradi — ro'yxat
  // `/owner/works`dan boyitilgan `WorkRow` (mijoz, avto, usta nomlari bilan),
  // bitta ish esa `WorkDetail`. Uni qatorlar orasiga qo'shib bo'lmaydi.
  //
  // Shuning uchun ro'yxatni ishning AYNI VAQTIGACHA kesamiz: ro'yxat eng
  // yangisidan tartiblangani uchun izlangan ish birinchi sahifaning boshida
  // bo'ladi va uning kuni avtomatik tanlanadi. Kun bo'yicha filtrlash
  // ishlamasdi: `date_to` "YYYY-MM-DD" ko'rinishida yarim tunga aylanadi va
  // backend `created_at <= date_to` deb solishtirib, o'sha kunning o'zini
  // chiqarib tashlardi (ustiga vaqt mintaqasi ham surib yuborardi).
  useEffect(() => {
    if (!focusWork) return;
    let alive = true;
    (async () => {
      try {
        const w = await api.getWork(focusWork.id);
        if (!alive) return;
        setFilters(EMPTY_FILTERS);
        setFocusCutoff(w.created_at);
        setDetails((d) => ({ ...d, [w.id]: w }));
        pendingExpandRef.current = w.id;
      } catch {
        /* ish o'chirilgan bo'lishi mumkin — jim qolamiz */
      }
    })();
    return () => {
      alive = false;
    };
  }, [focusWork?.id, focusWork?.seq]);

  const selectedGroup = groups.find((g) => g.key === selectedDate) ?? null;
  const hasMore = items.length < total;

  async function loadMore() {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const r = await api.works({ page: next, page_size: PAGE, ...queryParams });
      setItems((prev) => [...prev, ...r.items]);
      setTotal(r.total);
      setPage(next);
    } catch {
      /* empty */
    } finally {
      setLoadingMore(false);
    }
  }

  async function toggleDetail(id: number) {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!details[id]) {
      setDetails((d) => ({ ...d, [id]: "loading" }));
      try {
        const w = await api.getWork(id);
        setDetails((d) => ({ ...d, [id]: w }));
      } catch {
        setDetails((d) => {
          const copy = { ...d };
          delete copy[id];
          return copy;
        });
      }
    }
  }

  const update = (k: keyof Filters, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const filterCount = Object.values(filters).filter(Boolean).length;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setFocusCutoff(null);
  };

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-1.5">
          <Label htmlFor="from">{t("filters.from")}</Label>
          <Input
            id="from"
            type="date"
            value={filters.date_from}
            onChange={(e) => update("date_from", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="to">{t("filters.to")}</Label>
          <Input
            id="to"
            type="date"
            value={filters.date_to}
            onChange={(e) => update("date_to", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="svc">{t("filters.service")}</Label>
          <Select
            id="svc"
            value={filters.service_id}
            onChange={(e) => update("service_id", e.target.value)}
          >
            <option value="">{t("filters.all")}</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="usta">{t("filters.master")}</Label>
          <Select
            id="usta"
            value={filters.user_id}
            onChange={(e) => update("user_id", e.target.value)}
          >
            <option value="">{t("filters.all")}</option>
            {employees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
              </option>
            ))}
          </Select>
        </div>
        {hasDebts && (
          <div className="space-y-1.5">
            <Label htmlFor="pay">{t("filters.payment")}</Label>
            <Select
              id="pay"
              value={filters.tolov_holati}
              onChange={(e) => update("tolov_holati", e.target.value)}
            >
              <option value="">{t("filters.all")}</option>
              <option value="tolandi">{t("form.paid")}</option>
              <option value="qisman">{t("form.partial")}</option>
              <option value="tolanmagan">{t("form.unpaid")}</option>
            </Select>
          </div>
        )}
      </PageFilterPopover>
    ),
    [filters, services, employees, filterCount, hasDebts],
  );

  const exportWorks = async () => {
    const q = new URLSearchParams();
    if (filters.date_from) q.set("date_from", filters.date_from);
    if (filters.date_to) q.set("date_to", filters.date_to);
    if (filters.service_id) q.set("service_id", filters.service_id);
    if (filters.user_id) q.set("user_id", filters.user_id);
    if (filters.tolov_holati) q.set("tolov_holati", filters.tolov_holati);
    if (selectedBranchId != null) q.set("branch_id", String(selectedBranchId));
    const qs = q.toString();
    await downloadFile(`/owner/works/export.xlsx${qs ? `?${qs}` : ""}`, t("exportFileName"));
  };

  const actionsNode = useMemo(
    () => (
      <>
        <ExportButton onExport={exportWorks} />
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus />
          <span className="hidden sm:inline">{t("actions.newWork")}</span>
        </Button>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, selectedBranchId],
  );

  usePageHeader({ filters: filterNode, actions: actionsNode, filterCount });

  return (
    <div className="works-page space-y-4">
      <Dialog
        open={creating}
        onOpenChange={(o) => !o && setCreating(false)}
        contentClassName="overflow-hidden sm:max-w-2xl"
      >
        {creating && (
          <WorkForm
            services={services}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              setReload((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      {methodEdit && (
        <WorkPaymentCorrectionDialog
          open={!!methodEdit}
          onOpenChange={(o) => !o && setMethodEdit(null)}
          current={methodEdit.current}
          totalPaid={methodEdit.totalPaid}
          partyLabel={methodEdit.party}
          onSave={async (payments) => {
            const updated = await api.correctWorkPayments(methodEdit.id, payments);
            // Ochiq tafsilot SERVER qaytargan qiymat bilan yangilanadi.
            setDetails((d) => {
              const cur = d[methodEdit.id];
              if (!cur || cur === "loading") return d;
              return {
                ...d,
                [methodEdit.id]: {
                  ...cur,
                  tolov_usuli: updated.tolov_usuli,
                  payments: updated.payments,
                },
              };
            });
            // Ro'yxat, to'lov xulosasi va usul bo'linishi qayta o'qiladi.
            // `pendingExpandRef` shart: qayta yuklash effekti `expandedId` ni
            // undan tiklaydi, aks holda endigina tahrirlangan qator yopilib
            // qolardi.
            pendingExpandRef.current = methodEdit.id;
            setReload((x) => x + 1);
          }}
        />
      )}

      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Joriy filtrdagi to'liq to'lanmagan ishlar SONI.

          ⚠️ Qoldiqlar YIG'INDISI ataylab olib tashlandi: u ikkinchi,
          kelishmaydigan "jami qarz" edi. Balansga yozilgan to'lov hujjatning
          `tolangan_summa` siga tegmaydi, ya'ni hisob-kitobini yopgan mijozning
          ishi bu yig'indida hamon qarzdek turardi va bu sahifa bosh sahifadan
          boshqa raqam ko'rsatardi. "Bizga qancha qarz" — yagona raqam, u
          Qarzlar sahifasida (daftar neti). */}
      {hasDebts && summary != null && summary.count > 0 && (
        <Card className="works-unpaid-warning border-destructive/30 bg-destructive/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <span className="text-sm text-muted-foreground">
              {t("summary.unpaidCount", { count: summary.count })}
            </span>
            <Button variant="link" size="sm" className="h-auto p-0" onClick={onOpenDebts}>
              {t("summary.seeDebtsPage")} →
            </Button>
          </CardContent>
        </Card>
      )}

      {/* "Pul qanday kirdi" — qarz xulosasidan MUSTAQIL: `hasDebts` gate'idan
          TASHQARIDA, chunki usul barcha tariflarda mavjud. */}
      <PaymentMethodBreakdown
        data={methods}
        title={t("common:paymentMethod.worksTitle")}
        tooltip={t("common:paymentMethod.worksTooltip")}
        knownMethodsOnly
      />

      {loading ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={<FileSearch />}
              title={t("empty.title")}
              description={t("empty.description")}
              action={
                <Button onClick={() => setCreating(true)}>
                  <Plus />
                  {t("actions.newWork")}
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
          <DateColumn
            groups={groups.map((g) => ({
              key: g.key,
              total: g.total,
              count: g.items.length,
              repISO: g.repISO,
              debt: g.items.reduce((s, w) => s + workDebt(w), 0),
            }))}
            selectedKey={selectedDate}
            onSelect={setSelectedDate}
            labelOf={(iso) => fmtDateLong(iso)}
            countLabel={(n) => t("ledger.count", { count: n })}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingMore={loadingMore}
            loadMoreLabel={t("ledger.loadMore")}
          />

          <Card className="works-ledger-card">
            <CardContent className="p-0">
              {selectedGroup && (
                <>
                  <div className="border-b p-3">
                    <div className="text-sm font-semibold">{fmtDateLong(selectedGroup.repISO)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("ledger.count", { count: selectedGroup.items.length })} ·{" "}
                      {fmtMoney(selectedGroup.total)}
                    </div>
                  </div>

                  {/* Desktop: jadval, qatorni bosganda inline tafsilot ochiladi */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[720px] text-sm">
                      <thead className="bg-muted/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="w-8 px-2 py-2"></th>
                          <th className="px-2 py-2 text-left">{t("table.time")}</th>
                          <th className="px-2 py-2 text-left">{t("table.client")}</th>
                          <th className="px-2 py-2 text-left">{t("table.car")}</th>
                          <th className="px-2 py-2 text-left">{t("table.service")}</th>
                          <th className="px-2 py-2 text-left">{t("table.master")}</th>
                          <th className="px-2 py-2 text-left">{t("table.note")}</th>
                          {hasDebts && (
                            <th className="px-2 py-2 text-left">{t("table.payment")}</th>
                          )}
                          <th className="px-2 py-2 text-right">{t("table.price")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedGroup.items.map((w) => {
                          const open = expandedId === w.id;
                          return (
                            <WorkRowDesktop
                              key={w.id}
                              w={w}
                              open={open}
                              detail={details[w.id]}
                              onToggle={() => toggleDetail(w.id)}
                              hasDebts={hasDebts}
                              onOpenDebts={onOpenDebts}
                              onCorrectMethod={(current) =>
                                setMethodEdit({
                                  id: w.id,
                                  party: `${w.client_name} · ${w.plate_number}`,
                                  current,
                                  totalPaid: persistedPaidTotal(current),
                                })
                              }
                              t={t}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobil: kartalar, bosganda inline tafsilot */}
                  <div className="divide-y md:hidden">
                    {selectedGroup.items.map((w) => (
                      <WorkCardMobile
                        key={w.id}
                        w={w}
                        open={expandedId === w.id}
                        detail={details[w.id]}
                        onToggle={() => toggleDetail(w.id)}
                        hasDebts={hasDebts}
                        onOpenDebts={onOpenDebts}
                        onCorrectMethod={(current) =>
                          setMethodEdit({
                            id: w.id,
                            party: `${w.client_name} · ${w.plate_number}`,
                            current,
                            totalPaid: persistedPaidTotal(current),
                          })
                        }
                        t={t}
                      />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

type TFn = ReturnType<typeof useTranslation>["t"];

function WorkDetailBody({
  detail,
  onOpenDebts,
  onCorrectMethod,
  t,
}: {
  detail: WorkDetail | "loading" | undefined;
  onOpenDebts?: () => void;
  /** Joriy taqsimot bilan chaqiriladi — dialog sahifa darajasida turadi. */
  onCorrectMethod?: (current: WorkPayment[]) => void;
  t: TFn;
}) {
  if (detail === undefined || detail === "loading") {
    return <div className="px-3 py-3 text-sm text-muted-foreground">{t("detail.loading")}</div>;
  }
  const paidLabel = paymentLabel(detail.tolov_holati, t);
  const unpaid = detail.tolov_holati !== "tolandi" && detail.status === "active";
  return (
    <div className="space-y-3 px-3 py-3">
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("detail.parts")}
        </div>
        {detail.parts.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("detail.noParts")}</div>
        ) : (
          <div className="divide-y rounded-md border">
            {detail.parts.map((p) => (
              <div key={p.movement_id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
                <span className="min-w-0 flex-1 truncate">{p.product_nomi}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {Number(p.miqdor)} × {fmtMoney(p.birlik_narx)}
                </span>
                <span className="w-28 shrink-0 text-right font-medium tabular-nums">
                  {fmtMoney(p.jami_summa)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("detail.payment")}:</span>
          {paymentBadge(detail.tolov_holati, paidLabel)}
        </span>
        <span>
          <span className="text-muted-foreground">{t("detail.paid")}:</span>{" "}
          <span className="font-medium tabular-nums">{fmtMoney(detail.tolangan_summa)}</span>
        </span>
        <WorkPaymentValue
          payments={detail.payments}
          // Bekor qilingan ishda to'g'rilash yo'q — server ham 409
          // `work_cancelled` beradi (sotuv jurnalidagi bilan bir xil qoida).
          onCorrect={
            onCorrectMethod && detail.status === "active"
              ? () => onCorrectMethod(detail.payments)
              : undefined
          }
        />
        {detail.next_service_km != null && (
          <span>
            <span className="text-muted-foreground">{t("detail.nextKm")}:</span>{" "}
            <Mono className="text-success">{fmtInt(detail.next_service_km)}</Mono>
          </span>
        )}
        {/* To'lovni qabul qilish Qarzlar sahifasida — bu yerda yangi qarz
            ekrani QURILMAYDI, mavjudiga yo'naltiramiz. */}
        {unpaid && onOpenDebts && (
          <button
            type="button"
            className="text-sm font-medium text-primary hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDebts();
            }}
          >
            {t("detail.goToDebts")}
          </button>
        )}
      </div>
      {detail.note && (
        <div className="text-sm">
          <span className="text-muted-foreground">{t("detail.note")}:</span> {detail.note}
        </div>
      )}
    </div>
  );
}

function WorkRowDesktop({
  w,
  open,
  detail,
  onToggle,
  hasDebts,
  onOpenDebts,
  onCorrectMethod,
  t,
}: {
  w: WorkRow;
  open: boolean;
  detail: WorkDetail | "loading" | undefined;
  onToggle: () => void;
  hasDebts: boolean;
  onOpenDebts?: () => void;
  onCorrectMethod?: (current: WorkPayment[]) => void;
  t: TFn;
}) {
  const debt = workDebt(w);
  return (
    <>
      <tr
        className={cn("cursor-pointer border-t transition-colors hover:bg-accent", open && "bg-accent/60")}
        onClick={onToggle}
      >
        <td className="px-2 py-2 text-muted-foreground">
          <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
        </td>
        <td className="px-2 py-2 text-muted-foreground tabular-nums">{fmtTime(w.created_at)}</td>
        <td className="px-2 py-2 font-medium">{w.client_name}</td>
        <td className="px-2 py-2">
          <Plate>{w.plate_number}</Plate>
        </td>
        <td className="px-2 py-2">{w.service_name}</td>
        <td className="px-2 py-2 text-muted-foreground">{w.master_name}</td>
        <td className="max-w-[16rem] truncate px-2 py-2 text-muted-foreground">{w.note}</td>
        {hasDebts && (
          <td className="px-2 py-2">{paymentBadge(w.tolov_holati, paymentLabel(w.tolov_holati, t))}</td>
        )}
        <td className="px-2 py-2 text-right tabular-nums">
          <div className="font-semibold">{fmtMoney(workTotal(w))}</div>
          {debt > 0 && (
            <div className="text-xs font-medium text-destructive">
              {t("table.debt")}: {fmtMoney(debt)}
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-t bg-muted/30">
          <td colSpan={hasDebts ? 9 : 8} className="p-0">
            <WorkDetailBody
              detail={detail}
              onOpenDebts={onOpenDebts}
              onCorrectMethod={onCorrectMethod}
              t={t}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function WorkCardMobile({
  w,
  open,
  detail,
  onToggle,
  hasDebts,
  onOpenDebts,
  onCorrectMethod,
  t,
}: {
  w: WorkRow;
  open: boolean;
  detail: WorkDetail | "loading" | undefined;
  onToggle: () => void;
  hasDebts: boolean;
  onOpenDebts?: () => void;
  onCorrectMethod?: (current: WorkPayment[]) => void;
  t: TFn;
}) {
  const debt = workDebt(w);
  return (
    <div>
      <button type="button" className="w-full space-y-1.5 p-4 text-left" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="font-medium">{w.client_name}</div>
          <div className="text-xs text-muted-foreground tabular-nums">{fmtTime(w.created_at)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <Plate>{w.plate_number}</Plate>
          <span>·</span>
          <span>{w.service_name}</span>
          <span>·</span>
          <span>{w.master_name}</span>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{w.note}</span>
          {hasDebts && paymentBadge(w.tolov_holati, paymentLabel(w.tolov_holati, t))}
          <span className="text-right tabular-nums">
            <span className="font-semibold">{fmtMoney(workTotal(w))}</span>
            {debt > 0 && (
              <span className="block text-xs font-medium text-destructive">
                {t("table.debt")}: {fmtMoney(debt)}
              </span>
            )}
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t bg-muted/30">
          <WorkDetailBody
            detail={detail}
            onOpenDebts={onOpenDebts}
            onCorrectMethod={onCorrectMethod}
            t={t}
          />
        </div>
      )}
    </div>
  );
}

// ============================ Yangi ish formasi ============================

type PartLine = { product: Product; miqdor: string; sotuv_narx: string };
type SelectedService = { service: ServiceOut; price: string };

function WorkForm({
  services,
  onCancel,
  onSaved,
}: {
  services: ServiceOut[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation("works");
  const { selectedBranchId } = useBranch();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState<ClientDetails[]>([]);
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("+998");
  const [newPlate, setNewPlate] = useState("");
  const [carId, setCarId] = useState("");
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [selectedMechanics, setSelectedMechanics] = useState<EmployeeRow[]>([]);
  const [openMultiSelect, setOpenMultiSelect] = useState<"services" | "mechanics" | "payments" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Ishga biriktirilgan tovarlar — faqat pro+ (tovars).
  const hasTovars = useFeature("tovars");
  const [products, setProducts] = useState<Product[]>([]);
  const [parts, setParts] = useState<PartLine[]>([]);

  // Server tarif matritsasida qarz daftari barcha tariflarda bor. Feature
  // tekshiruvi qoladi: yuklanish paytidagi mavjud fail-open oqim buzilmaydi.
  const hasDebts = useFeature("qarzlar");
  const [paidTouched, setPaidTouched] = useState(false);
  // To'lov usuli — sukut bo'yicha naqd: bu yerda odam aynan hozir to'lovni
  // yozyapti. Bo'sh ("yozilmagan") tanlov ro'yxatda qoladi. Hech narsa
  // to'lanmagan ishda umuman yuborilmaydi (server ham rad etadi).
  const [paymentLines, setPaymentLines] = useState<WorkPaymentLine[]>([
    { method: "naqd", amount: "" },
  ]);
  const [debtDueDays, setDebtDueDays] = useState("");
  const { t: tc } = useTranslation("common");

  useEffect(() => {
    (async () => {
      try {
        setEmployees(await api.employees());
      } catch {
        setEmployees([]);
      }
      if (hasTovars) {
        try {
          setProducts(await api.products({ faol: true, branch_id: selectedBranchId ?? undefined }));
        } catch {
          /* empty */
        }
      }
    })();
  }, [hasTovars, selectedBranchId]);

  // Mijoz qidiruvi — debounce bilan avtomatik (alohida tugmasiz).
  useEffect(() => {
    if (client) return;
    const q = clientQuery.trim();
    if (!q) {
      setClientResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        setClientResults(await api.searchClients(q));
      } catch {
        setClientResults([]);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [clientQuery, client]);

  const partsTotal = parts.reduce(
    (s, p) => s + (Number(p.miqdor) || 0) * (Number(p.sotuv_narx) || 0),
    0,
  );
  const serviceTotal = selectedServiceTotal(selectedServices);
  const total = serviceTotal + partsTotal;

  // To'langan: tegilmaguncha Jamiga ergashadi, tozalansa yana ergashadi.
  // Qarz esa har doim SERVER tomonda hisoblanadi — bu faqat ko'rsatkich.
  const singlePaidDisplay = displayedSingleAmount(paymentLines, total, paidTouched);
  const paidNum = aggregatePaid(paymentLines, total, paidTouched);
  const qarz = Math.max(0, total - paidNum);
  const overpaid = paidNum > total;
  const invalidSplit = invalidPaymentLines(paymentLines);

  function onPaidChange(method: PaymentMethod, v: string) {
    if (v === "") {
      if (paymentLines.length === 1) setPaidTouched(false);
      setPaymentLines((lines) => lines.map((line) =>
        line.method === method ? { ...line, amount: "" } : line
      ));
      return;
    }
    setPaidTouched(true);
    setPaymentLines((lines) => lines.map((line) =>
      line.method === method ? { ...line, amount: v } : line
    ));
  }

  function togglePaymentMethod(method: PaymentMethod) {
    const wasTouched = paidTouched;
    setPaidTouched(true);
    setPaymentLines((current) => {
      return togglePaymentLine(current, method, total, wasTouched);
    });
  }

  function addProduct(p: Product) {
    setParts((ps) => {
      // Allaqachon qo'shilgan tovar — dublikat satr emas, mavjudi oshadi.
      const i = ps.findIndex((x) => x.product.id === p.id);
      if (i >= 0) {
        const copy = [...ps];
        copy[i] = { ...copy[i], miqdor: String((Number(copy[i].miqdor) || 0) + 1) };
        return copy;
      }
      // Tovar narxi: kasrni olib tashlaymiz ("15000.00" → "15000"), aks holda
      // MoneyInput nuqtani yo'qotib ×100 ko'rsatardi.
      return [
        ...ps,
        { product: p, miqdor: "1", sotuv_narx: String(Math.round(Number(p.sotuv_narx))) },
      ];
    });
  }

  function setPart(i: number, patch: Partial<PartLine>) {
    setParts((ps) => ps.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }

  function cancelNewClient() {
    setAddingNew(false);
    setNewName("");
    setNewPhone("+998");
    setNewPlate("");
  }

  async function createAndSelectClient() {
    if (!newName.trim() || isBlankPhoneInput(newPhone) || !newPlate.trim()) {
      setErr(t("errors.newClientName"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const cl = await api.createClient({ full_name: newName.trim(), phone: newPhone.trim() });
      await api.createCar({ client_id: cl.id, plate_number: newPlate.trim() });
      const details = await api.clientDetails(cl.id);
      setClient(details);
      if (details.cars.length === 1) setCarId(String(details.cars[0].id));
      cancelNewClient();
    } catch (e) {
      setErr((e as { message?: string }).message || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  async function pickClient(c: ClientDetails) {
    try {
      const details = await api.clientDetails(c.id);
      setClient(details);
      setClientResults([]);
      setClientQuery("");
      if (details.cars.length === 1) setCarId(String(details.cars[0].id));
    } catch (e) {
      setErr((e as { message?: string }).message || t("errors.generic"));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!carId || selectedServices.length === 0 || selectedMechanics.length === 0) {
      setErr(t("errors.selectRequired"));
      return;
    }
    if (overpaid) {
      setErr(t("form.errOverpaid"));
      return;
    }
    if (invalidSplit) {
      setErr(t("form.errSplitAmount"));
      return;
    }
    if (qarz > 0 && (!debtDueDays || Number(debtDueDays) <= 0)) {
      setErr(t("form.errDebtDueDays"));
      return;
    }
    const payments = paymentPayload(paymentLines, total, paidTouched);
    setBusy(true);
    setErr(null);
    try {
      await api.createWork({
        car_id: Number(carId),
        service_id: selectedServices[0].service.id,
        services: selectedServices.map((item) => ({
          service_id: item.service.id,
          price: item.price || "0",
        })),
        user_id: selectedMechanics[0].id,
        mechanic_ids: selectedMechanics.map((item) => item.id),
        price: String(serviceTotal),
        note: note.trim() || null,
        // Qarzning yagona manbasi — `tolov_holati` yubormaymiz, uni server
        // jamiga qarab o'zi hosil qiladi.
        tolangan_summa: String(paidNum),
        payments,
        // Bitta usul eski mijozlar uchun scalar mirror bo'lib qoladi. Splitda
        // server uni NULL qiladi va haqiqiy taqsimot `payments`da saqlanadi.
        ...(payments.length === 1 ? { tolov_usuli: payments[0].tolov_usuli } : {}),
        ...(qarz > 0 ? { debt_due_days: Number(debtDueDays) } : {}),
        parts: parts
          .filter((p) => Number(p.miqdor) > 0)
          .map((p) => ({
            product_id: p.product.id,
            miqdor: p.miqdor,
            sotuv_narx: p.sotuv_narx || "0",
          })),
      });
      toast.success(t("toast.created"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  function toggleService(service: ServiceOut) {
    setSelectedServices((current) => {
      const existing = current.find((item) => item.service.id === service.id);
      const line = existing ?? {
        service,
        price: service.narx == null || service.narx === ""
          ? "0"
          : String(Math.round(Number(service.narx))),
      };
      return toggleUnique(current, line, (item) => item.service.id);
    });
  }

  function toggleMechanic(mechanic: EmployeeRow) {
    setSelectedMechanics((current) => toggleUnique(current, mechanic, (item) => item.id));
  }

  return (
    <DialogForm onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{t("form.title")}</DialogTitle>
        <DialogDescription>{t("form.description")}</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex-1 space-y-4">
        <div className="space-y-1.5">
          <Label>{t("form.client")}</Label>
          {client ? (
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="font-medium">
                {client.full_name} <span className="text-muted-foreground">· {formatUzPhone(client.phone)}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setClient(null);
                  setCarId("");
                }}
              >
                {t("form.change")}
              </Button>
            </div>
          ) : addingNew ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  disabled={busy}
                  placeholder={t("form.newNamePlaceholder")}
                />
                <Input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  disabled={busy}
                  placeholder={t("form.newPhonePlaceholder")}
                  inputMode="tel"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={cancelNewClient}
                  disabled={busy}
                  aria-label={t("form.cancel")}
                >
                  <X className="size-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={newPlate}
                  onChange={(e) => setNewPlate(e.target.value)}
                  disabled={busy}
                  placeholder={t("form.newPlatePlaceholder")}
                />
                <Button type="button" onClick={createAndSelectClient} disabled={busy} className="shrink-0">
                  {t("form.addClient")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                  placeholder={t("form.clientSearchPlaceholder")}
                  disabled={busy}
                />
                {clientResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-40 divide-y overflow-y-auto rounded-md border bg-card shadow-lg">
                    {clientResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => pickClient(c)}
                      >
                        {c.full_name} <span className="text-muted-foreground">· {formatUzPhone(c.phone)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddingNew(true)}
                disabled={busy}
                className="shrink-0"
              >
                <Plus />
                {t("form.addClient")}
              </Button>
            </div>
          )}
        </div>

        {client && (
          <div className="space-y-1.5">
            <Label htmlFor="w-car">{t("form.car")}</Label>
            <Select
              id="w-car"
              value={carId}
              onChange={(e) => setCarId(e.target.value)}
              disabled={busy}
              required
            >
              <option value="">{t("form.select")}</option>
              {client.cars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.plate_number}
                  {c.model ? ` · ${c.model}` : ""}
                </option>
              ))}
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("form.serviceLabel")}</Label>
            <MultiSelectDropdown
              id="work-services"
              open={openMultiSelect === "services"}
              onOpenChange={(open) => setOpenMultiSelect(open ? "services" : null)}
              disabled={busy}
              placeholder={t("form.select")}
              selected={selectedServices}
              options={services}
              selectedKey={(item) => item.service.id}
              optionKey={(item) => item.id}
              optionLabel={(item) => item.name}
              onOptionToggle={toggleService}
              renderSelected={(item, index) => (
                <div key={item.service.id} className="flex items-center gap-2 rounded px-2 py-1">
                  <span className="min-w-0 flex-1 truncate text-sm">{item.service.name}</span>
                  <MoneyInput
                    value={item.price}
                    onChange={(value) => setSelectedServices((current) => current.map((row, rowIndex) =>
                      rowIndex === index ? { ...row, price: value } : row
                    ))}
                    disabled={busy}
                    className="h-8 w-28 text-right"
                    aria-label={`${item.service.name} ${t("form.price")}`}
                  />
                  <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0"
                    onClick={() => toggleService(item.service)} disabled={busy}
                    aria-label={`${item.service.name} ${t("form.cancel")}`}>
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("form.master")}</Label>
            <MultiSelectDropdown
              id="work-mechanics"
              open={openMultiSelect === "mechanics"}
              onOpenChange={(open) => setOpenMultiSelect(open ? "mechanics" : null)}
              disabled={busy}
              placeholder={t("form.select")}
              selected={selectedMechanics}
              options={employees}
              selectedKey={(mechanic) => mechanic.id}
              optionKey={(mechanic) => mechanic.id}
              optionLabel={(mechanic) => mechanic.full_name}
              onOptionToggle={toggleMechanic}
              renderSelected={(mechanic) => (
                <div key={mechanic.id} className="flex items-center gap-2 rounded px-2 py-1.5">
                  <span className="min-w-0 flex-1 truncate text-sm">{mechanic.full_name}</span>
                  <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0"
                    onClick={() => toggleMechanic(mechanic)} disabled={busy}
                    aria-label={`${mechanic.full_name} ${t("form.cancel")}`}>
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="w-price">{t("form.price")}</Label>
            <MoneyInput id="w-price" value={String(serviceTotal)} onChange={() => {}} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="w-note">{t("form.note")}</Label>
            <Input
              id="w-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        {hasTovars && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <Label>{t("form.partsTitle")}</Label>
              {partsTotal > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t("form.partsTotal", { total: fmtMoney(partsTotal) })}
                </span>
              )}
            </div>
            {/* `excludeIds` ataylab berilmaydi: qo'shilgan tovar ro'yxatda
                qolib, qayta tanlanganda mavjud satr miqdorini oshiradi. */}
            <ProductCombobox
              products={products}
              onPick={addProduct}
              disabled={busy}
              placeholder={t("form.selectProduct")}
            />
            {parts.map((p, i) => (
              <div key={p.product.id} className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium" title={p.product.nomi}>
                    {p.product.nomi}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {Number(p.product.joriy_ostatka)} {p.product.birlik}
                  </div>
                </div>
                <Input
                  className="w-20"
                  type="number"
                  min="0"
                  step="0.001"
                  placeholder={t("form.qty")}
                  value={p.miqdor}
                  onChange={(e) => setPart(i, { miqdor: e.target.value })}
                  disabled={busy}
                />
                <MoneyInput
                  className="w-24"
                  placeholder={t("form.partPrice")}
                  value={p.sotuv_narx}
                  onChange={(v) => setPart(i, { sotuv_narx: v })}
                  disabled={busy}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setParts((ps) => ps.filter((_, idx) => idx !== i))}
                  disabled={busy}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Jami + To'langan. To'langan ataylab ikkinchi darajali: inline
            yorliq, kichik maydon — asosiy harakat emas, tuzatish. */}
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex items-end justify-end gap-3">
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                {t("form.total")}
              </div>
              <div className="text-2xl font-bold tabular-nums">{fmtMoney(total)}</div>
            </div>
          </div>

          {hasDebts && (
            <>
              <div className="space-y-1.5">
                <Label>{tc("paymentMethod.label")}</Label>
                <MultiSelectDropdown
                  id="work-payments"
                  open={openMultiSelect === "payments"}
                  onOpenChange={(open) => setOpenMultiSelect(open ? "payments" : null)}
                  disabled={busy}
                  placeholder={t("form.selectPaymentMethods")}
                  selected={paymentLines}
                  options={PAYMENT_METHODS}
                  selectedKey={(line) => line.method}
                  optionKey={(method) => method}
                  optionLabel={(method) => tc(`paymentMethod.${method}`)}
                  onOptionToggle={togglePaymentMethod}
                  renderSelected={(line) => (
                    <div key={line.method} className="flex items-center gap-2 rounded px-2 py-1.5">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {tc(`paymentMethod.${line.method}`)}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0"
                        onClick={() => togglePaymentMethod(line.method)}
                        disabled={busy}
                        aria-label={`${tc(`paymentMethod.${line.method}`)} ${t("form.cancel")}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                />
              </div>
              <div className="space-y-2">
                {paymentLines.map((line, index) => (
                  <div key={line.method} className="flex items-center justify-end gap-2">
                    <Label htmlFor={`w-paid-${line.method}`} className="text-xs text-muted-foreground">
                      {paymentLines.length === 1
                        ? t("form.paidAmount")
                        : t("form.paidAmountFor", { method: tc(`paymentMethod.${line.method}`) })}
                    </Label>
                    <MoneyInput
                      id={`w-paid-${line.method}`}
                      className="w-32 text-right"
                      value={index === 0 && paymentLines.length === 1 ? singlePaidDisplay : line.amount}
                      onChange={(value) => onPaidChange(line.method, value)}
                      disabled={busy}
                    />
                  </div>
                ))}
              </div>
              {qarz > 0 && (
                <>
                  <div className="text-right text-sm font-medium text-destructive tabular-nums">
                    {t("form.qarz")}: {fmtMoney(qarz)}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Label htmlFor="w-debt-days" className="text-xs text-muted-foreground">
                      {t("form.debtDueDays")}
                    </Label>
                    <Input
                      id="w-debt-days"
                      type="number"
                      min="1"
                      step="1"
                      className="w-32 text-right"
                      value={debtDueDays}
                      onChange={(event) => setDebtDueDays(event.target.value.replace(/\D/g, ""))}
                      disabled={busy}
                    />
                  </div>
                </>
              )}
              {overpaid && (
                <div className="text-right text-sm font-medium text-destructive">
                  {t("form.errOverpaid")}
                </div>
              )}
              {invalidSplit && (
                <div className="text-right text-sm font-medium text-destructive">
                  {t("form.errSplitAmount")}
                </div>
              )}
            </>
          )}
        </div>

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter className="sticky bottom-0 z-10 shrink-0">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("form.cancel")}
        </Button>
        <Button type="submit" disabled={busy || overpaid || invalidSplit}>
          {busy ? t("form.saving") : t("form.save")}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}
